import { useEffect, useMemo, useRef } from 'react'

import { usePathname, useSearchParams } from 'next/navigation'

import { RoleStatus } from '@b-stock/account-api-client'
import {
  sendGTMEvent,
  useAccessTokenRoles,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import {
  getNumberOfUnitsInLot,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  getSelectedQuoteQuery,
  type OrchestratedListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'

import getTypicalSellingPriceAnalyticsData from '@/buyer-portal/components/features/listing/helpers/getTypicalSellingPriceAnalyticsData'
import useAutoQuoteGeneration from '@/buyer-portal/components/features/listing/hooks/useAutoQuoteGeneration'
import { useCostContextAnalyticsProperties } from '@/buyer-portal/components/features/listing/hooks/useCostContextAnalyticsProperties'
import { useFdActiveGroup } from '@/buyer-portal/components/features/listing/hooks/useFdActiveGroup'
import { useProblemFreeOrderRateAnalytics } from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import { getAppConfig } from '@/buyer-portal/config/appConfig'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { attAccessRouteProperty } from '@/components/storefrontAccess/attAccessRoute'
import { useAttAccessRoute } from '@/components/storefrontAccess/useAttAccessRoute'
import { FeatureFlags } from '@/helpers/featureFlags'
import { toFirstDibsSegmentProps } from '@/helpers/telemetry/firstDibsSegmentProps'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'

export const useTrackPageViewed = ({
  referralUrl,
  listing,
}: {
  referralUrl: string
  listing: OrchestratedListingDetails
}) => {
  const analytics = useAnalytics()
  const { pageViewed, track } = analytics
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const queryString = searchParams?.toString()

  const hasFiredRef = useRef<string | null>(null)
  // Signalcraft shipping-quote proposal: set to the page-view's key when it
  // fired with shipping_quote_status: 'quote_pending', so the effect below
  // knows to watch for the quote settling on *this* page view (not a stale
  // one from before the buyer navigated away).
  const pendingShippingQuoteKeyRef = useRef<string | null>(null)
  const hasFiredShippingQuoteResolvedRef = useRef<string | null>(null)

  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  // The same open-window rule as the LDP's First Dibs banner (flag included)
  const { activeGroup: fdActiveGroup } = useFdActiveGroup(listing.firstDibs)
  const ccSurfacingEnabled = useFlag(FeatureFlags.ENABLE_CC_SURFACING)
  const enableSellingPriceCalculation = useFlag(
    FeatureFlags.ENABLE_BID_BOX_SELLING_PRICE_CALCULATION
  )
  const enableSellingPriceCalculationCostco = useFlag(
    FeatureFlags.ENABLE_BID_BOX_SELLING_PRICE_CALCULATION_COSTCO
  )
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds, isError: isNewSellerQueryError } =
    newSellerStorefrontsQuery.useWithOpts([], {
      enabled: isNewSellerTreatment,
    })

  const typicalSellingPriceAnalytics = getTypicalSellingPriceAnalyticsData(
    listing,
    enableSellingPriceCalculation,
    enableSellingPriceCalculationCostco
  )

  const problemFreeOrderRateAnalytics =
    useProblemFreeOrderRateAnalytics(listing)

  // Fetch and format P2S data
  const p2sData = useListingP2SData(listing.lot.locationOriginalId)

  // Reuses the same cache-backed query the LDP action column already fetches
  // (AuctionActionColumnContent / OfferingActionColumn), so this never issues
  // its own network request. `enabled` must match those callers' isActiveBuyer
  // gate: React Query fetches a shared key if ANY observer enables it, so a
  // looser condition here would fire the request (and a 403) for every buyer
  // who isn't active, on every listing page view.
  const accountId = useDecodedAccessToken({ required: false })?.accountId
  const isActiveBuyer = useAccessTokenRoles()?.buyer === RoleStatus.Active
  const selectedQuoteQuery = getSelectedQuoteQuery.useWithOpts(
    [listing._id, accountId],
    { enabled: !!accountId && isActiveBuyer }
  )

  // Signalcraft shipping-quote proposal: a 4th call site for this hook,
  // already called from ListingShippingSummary, ShippingInformationCard, and
  // BidFormProvider, each with its own hasAttemptedAutoGeneration ref. The
  // shared handleNewQuoteSelection context (fe-core) itself guards against a
  // duplicate in-flight fetch (its own `_loading` ref), so a 4th caller
  // attempting alongside the others isn't new risk — same as the existing 3.
  const autoQuoteGeneration = useAutoQuoteGeneration(listing)

  // AT&T only: the access route from the decision the listing guard already
  // cached; undefined (no property) for every listing the guard doesn't check.
  const attAccess = useAttAccessRoute(listing)

  // Cost-context properties (current/minimum bid, fee, TLC) — shared
  // derivation with BidActions/QuickBidButtons, see the hook's docblock.
  // Neither this hook nor its underlying helper memoises its return value, so
  // wrap it here keyed on the primitive values — otherwise a new object
  // reference every render would re-evaluate the effect below on every
  // unrelated re-render (harmless, since hasFiredRef still guards the actual
  // firing, but unnecessary work).
  const rawCostContext = useCostContextAnalyticsProperties({
    listing,
    selectedQuote: selectedQuoteQuery.data,
  })
  // Deliberately keyed on rawCostContext's primitive values, not the object
  // itself, so this stays referentially stable across renders where nothing
  // actually changed.
  /* oxlint-disable eslint-plugin-react-hooks/exhaustive-deps */
  const costContext = useMemo(
    () => rawCostContext,
    [
      rawCostContext.isLoading,
      rawCostContext.properties.current_bid_usd,
      rawCostContext.properties.minimum_required_bid_usd,
      rawCostContext.properties.bstock_fee_usd,
      rawCostContext.properties.total_landed_cost_usd,
    ]
  )
  /* oxlint-enable eslint-plugin-react-hooks/exhaustive-deps */

  // Store initial values in refs to use in effect
  const initialValues = useRef({
    referralUrl,
    listing,
    pageViewed,
    typicalSellingPriceAnalytics,
    fmvBadgeFlag,
    problemFreeOrderRateAnalytics,
    ccSurfacingEnabled,
  })

  useEffect(() => {
    // Wait for P2S, address, and cost-context data to load before firing
    // analytics — see useCostContextAnalyticsProperties' isLoading docblock.
    if (
      p2sData.isLoading ||
      selectedQuoteQuery.isLoading ||
      costContext.isLoading ||
      attAccess.isLoading
    )
      return
    if (
      isNewSellerTreatment &&
      newSellerIds === undefined &&
      !isNewSellerQueryError
    )
      return

    const key = `${pathname}?${queryString}`
    if (hasFiredRef.current === key) return
    hasFiredRef.current = key
    const {
      referralUrl,
      listing,
      pageViewed,
      typicalSellingPriceAnalytics,
      fmvBadgeFlag,
      problemFreeOrderRateAnalytics,
      ccSurfacingEnabled,
    } = initialValues.current

    // Use listing title for page name, fallback to prettyId or generic name
    const pageName =
      listing.lot.title || listing.prettyId || 'listing_detail_page'

    pageViewed({
      screenName: 'listing_detail_page',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      referral_url: referralUrl,
      autocharge_listing: !!listing.autoPay,
      is_geofence_listing: !!listing.geofence,
      address_id: selectedQuoteQuery.data?.destination.id ?? null,
      is_new_bid_box_design: true,
      cc_tag_viewed: ccSurfacingEnabled && listing.isCreditCardEnabled === true,
      cc_eligibility_variant: ccSurfacingEnabled ? 'bundled' : 'control',
      is_total_landed_cost_displayed:
        !!listing.lot.retailValue ||
        (getNumberOfUnitsInLot(listing.lot) ?? 0) > 0,
      is_new_seller:
        isNewSellerTreatment &&
        (newSellerIds?.has(listing.storefrontId) ?? false),
      ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
      ...typicalSellingPriceAnalytics,
      ...problemFreeOrderRateAnalytics,
      ...costContext.properties,
      shipping_quote_status: autoQuoteGeneration.status,
      shipping_quote_amount_cents: autoQuoteGeneration.amountCents,
      shipping_quote_currency: autoQuoteGeneration.currency,
      ...attAccessRouteProperty(attAccess.attAccessRoute),
      ...toFirstDibsSegmentProps(fdActiveGroup, { withEarlyPrice: true }),
    })

    // Two-event design: fires exactly as above, no delay, so fast bouncers
    // are still captured. On a cache-cold visit the quote pipeline hasn't
    // settled yet — remember this page view's key so the effect below can
    // fire a follow-up shipping_quote_resolved once it does, without
    // mislabeling a later, unrelated page view as this one's resolution.
    if (autoQuoteGeneration.status === 'quote_pending') {
      pendingShippingQuoteKeyRef.current = key
    }

    const { platform } = getAppConfig()
    const marketplace = listing.storefrontId || listing.seller.storefront?.name

    sendGTMEvent({
      event: 'page_view',
      platform,
      marketplace,
      page_type: 'pdp',
      page_name: pageName,
    })
  }, [
    pathname,
    queryString,
    p2sData,
    selectedQuoteQuery,
    costContext,
    autoQuoteGeneration.status,
    autoQuoteGeneration.amountCents,
    autoQuoteGeneration.currency,
    isNewSellerTreatment,
    newSellerIds,
    isNewSellerQueryError,
    attAccess.isLoading,
    attAccess.attAccessRoute,
    fdActiveGroup,
  ])

  // Signalcraft shipping-quote proposal: shipping_quote_resolved. Fires at
  // most once per LDP view, only when the page view above fired with
  // quote_pending, once the auto-quote pipeline settles into a terminal
  // status. If the buyer navigates away before it settles, this simply
  // never fires — the bounced-while-pending cohort, not missing data.
  useEffect(() => {
    const pendingKey = pendingShippingQuoteKeyRef.current
    if (!pendingKey) return
    if (`${pathname}?${queryString}` !== pendingKey) return
    if (autoQuoteGeneration.status === null) return
    if (autoQuoteGeneration.status === 'quote_pending') return
    if (costContext.isLoading) return
    if (hasFiredShippingQuoteResolvedRef.current === pendingKey) return
    hasFiredShippingQuoteResolvedRef.current = pendingKey

    track('shipping_quote_resolved', {
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listing._id,
      shipping_quote_status: autoQuoteGeneration.status,
      shipping_quote_amount_cents: autoQuoteGeneration.amountCents,
      shipping_quote_currency: autoQuoteGeneration.currency,
      ...costContext.properties,
    })
  }, [
    pathname,
    queryString,
    listing._id,
    track,
    costContext,
    autoQuoteGeneration.status,
    autoQuoteGeneration.amountCents,
    autoQuoteGeneration.currency,
  ])
}
